import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Check } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  category?: string;
  disabled?: boolean;
  badge?: string;
}

interface CustomSelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  className?: string;
  buttonClassName?: string;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  options,
  onChange,
  className = '',
  buttonClassName = '',
  placeholder = 'Select option...',
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const SelectedIcon = selectedOption?.icon;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = 260;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const spaceBelow = windowHeight - rect.bottom;
    const spaceAbove = rect.top;

    const minWidth = 160;
    const menuWidth = Math.max(rect.width, minWidth);
    let left = rect.left;

    // Boundary check for horizontal overflow
    if (left + menuWidth > windowWidth - 12) {
      left = Math.max(12, windowWidth - menuWidth - 12);
    }

    const style: React.CSSProperties = {
      position: 'fixed',
      width: `${menuWidth}px`,
      left: `${left}px`,
      zIndex: 99999,
    };

    // Open upward if space below is insufficient and more space exists above
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      style.bottom = `${windowHeight - rect.top + 4}px`;
      style.maxHeight = `${Math.max(120, Math.min(spaceAbove - 16, dropdownHeight))}px`;
    } else {
      style.top = `${rect.bottom + 4}px`;
      style.maxHeight = `${Math.max(120, Math.min(spaceBelow - 16, dropdownHeight))}px`;
    }

    setMenuStyle(style);
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase().trim();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q) ||
        (opt.category && opt.category.toLowerCase().includes(q))
    );
  }, [options, searchable, searchQuery]);

  return (
    <div
      ref={containerRef}
      className={`relative text-left ${className.includes('w-') ? '' : 'inline-block'} ${className}`}
    >
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-600 focus:outline-none focus:border-orange-500 transition-all cursor-pointer select-none disabled:opacity-50 disabled:cursor-not-allowed ${buttonClassName}`}
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          {SelectedIcon && <SelectedIcon className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 flex-shrink-0" />}
          <span className="truncate capitalize">{selectedOption ? selectedOption.label : placeholder}</span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>

      {isOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="rounded-xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-2xl z-[99999] overflow-hidden flex flex-col border-t-orange-500/40 animate-in fade-in zoom-in-95 duration-100"
          >
            {searchable && (
              <div className="p-2 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/90 flex-none">
                <div className="relative flex items-center">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 pointer-events-none" />
                  <input
                    type="text"
                    autoFocus
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg px-2.5 py-1.5 pl-8 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 font-sans"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 text-gray-400 hover:text-gray-600 dark:hover:text-white p-0.5 cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-y-auto custom-scroll p-1 space-y-0.5 flex-1 max-h-56">
              {filteredOptions.length === 0 ? (
                <div className="py-3 text-center text-xs text-gray-400 dark:text-gray-500 italic">
                  No matching options
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = opt.value === value;
                  const OptIcon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={opt.disabled}
                      onClick={() => {
                        if (opt.disabled) return;
                        onChange(opt.value);
                        setIsOpen(false);
                        setSearchQuery('');
                      }}
                      className={`w-full text-left px-2.5 py-1.5 text-xs rounded-md transition-colors flex items-center justify-between group ${
                        opt.disabled
                          ? 'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-500'
                          : isSelected
                          ? 'text-orange-600 dark:text-orange-400 font-bold bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 cursor-pointer'
                          : 'text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate min-w-0">
                        {OptIcon && (
                          <OptIcon
                            className={`w-3.5 h-3.5 flex-shrink-0 ${isSelected ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'
                              }`}
                          />
                        )}
                        <span className="truncate capitalize">{opt.label}</span>
                        {opt.badge && (
                          <span className="text-[10px] text-gray-400 font-normal ml-1">
                            {opt.badge}
                          </span>
                        )}
                      </div>
                      {isSelected && <Check className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
